import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useProductData, productImageUrl } from "../context/ProductDataContext";

const norm = (value) =>
  (value || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

const productHaystack = (p) =>
  norm([
    p?.name,
    p?.brand,
    p?.subtitle,
    p?.seo?.metaTitle,
    p?.seo?.metaDescription,
    ...(Array.isArray(p?.tags) ? p.tags : []),
    ...(Array.isArray(p?.functionIds) ? p.functionIds : []),
    ...(Array.isArray(p?.apps) ? p.apps : []),
    ...(Array.isArray(p?.categoryIds) ? p.categoryIds : []),
    ...(Array.isArray(p?.compat?.protocols) ? p.compat.protocols : []),
    ...(Array.isArray(p?.compat?.ecosystems) ? p.compat.ecosystems : []),
  ].filter(Boolean).join(" "));

/**
 * SearchBar – context-alapú globál kereső a syncelt product index alapján.
 * - Kategóriák: /kategoriak/:id
 * - Termékek: /termek/:id
 */
export default function SearchBar() {
  const navigate = useNavigate();
  const { products, flatCategories, loading } = useProductData();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const categoryList = useMemo(
    () => (Array.isArray(flatCategories) ? flatCategories.map((c) => ({ ...c, type: "category" })) : []),
    [flatCategories]
  );

  const searchable = useMemo(() => {
    const cats = categoryList.map((c) => ({
      ...c,
      __key: `c:${c.id}`,
      __hay: norm(`${c.name || ""} ${c.id || ""}`),
    }));

    const prods = (Array.isArray(products) ? products : [])
      .filter((p) => p?.id || p?.slug)
      .map((p) => ({
        id: p.id || p.slug,
        name: p.name || "",
        brand: p.brand || "",
        subtitle: p.subtitle || "",
        image: productImageUrl(p),
        type: "product",
        __key: `p:${p.id || p.slug}`,
        __name: norm(p.name || ""),
        __brand: norm(p.brand || ""),
        __hay: productHaystack(p),
      }));

    return { cats, prods };
  }, [categoryList, products]);

  useEffect(() => {
    if (!query.trim() || loading) {
      setResults([]);
      setActiveIdx(-1);
      return undefined;
    }

    const handle = setTimeout(() => {
      const q = norm(query);
      if (!q) {
        setResults([]);
        setActiveIdx(-1);
        return;
      }

      const catStarts = [];
      const catContains = [];
      for (const c of searchable.cats) {
        if (!c.__hay) continue;
        if (c.__hay.startsWith(q)) catStarts.push(c);
        else if (c.__hay.includes(q)) catContains.push(c);
      }

      const productMatches = [];
      for (const p of searchable.prods) {
        if (!p.__hay) continue;
        if (p.__name.startsWith(q)) productMatches.push({ ...p, __score: 100 });
        else if (p.__brand.startsWith(q)) productMatches.push({ ...p, __score: 80 });
        else if (p.__name.includes(q)) productMatches.push({ ...p, __score: 70 });
        else if (p.__hay.includes(q)) productMatches.push({ ...p, __score: 50 });
      }

      productMatches.sort((a, b) => b.__score - a.__score || a.name.localeCompare(b.name, "hu"));

      const merged = [...catStarts, ...catContains, ...productMatches].slice(0, 30);
      setResults(merged);
      setActiveIdx(merged.length ? 0 : -1);
    }, 180);

    return () => clearTimeout(handle);
  }, [query, loading, searchable]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const go = useCallback(
    (item) => {
      if (!item) return;
      setOpen(false);
      setQuery("");
      setResults([]);
      setActiveIdx(-1);

      if (item.type === "category") navigate(`/kategoriak/${item.id}`);
      else navigate(`/termek/${item.id}`);
    },
    [navigate]
  );

  const onKeyDown = useCallback(
    (e) => {
      if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
        setOpen(true);
        return;
      }

      if (e.key === "Escape") {
        setOpen(false);
        return;
      }

      if (!results.length) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(results.length - 1, i + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        go(results[activeIdx]);
      }
    },
    [open, results, activeIdx, go]
  );

  return (
    <div ref={wrapRef} className="relative w-full max-w-md">
      <input
        ref={inputRef}
        type="text"
        placeholder="Keresés termékre, márkára, appra, protokollra…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        onFocus={() => setOpen(true)}
        className="w-full border rounded px-4 py-2 text-sm text-gray-800"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls="global-search-listbox"
      />

      {open && query.trim() && (
        <div
          id="global-search-listbox"
          role="listbox"
          className="absolute bg-white border shadow-lg w-full max-h-96 overflow-y-auto z-50 mt-1 rounded-xl text-sm"
        >
          {results.length === 0 ? (
            <div className="px-4 py-3 text-gray-500">Nincs találat.</div>
          ) : (
            results.map((item, idx) => {
              const isActive = idx === activeIdx;
              return (
                <button
                  key={item.__key}
                  role="option"
                  aria-selected={isActive}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => go(item)}
                  onMouseEnter={() => setActiveIdx(idx)}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-left ${
                    isActive ? "bg-blue-50" : "hover:bg-gray-50"
                  }`}
                >
                  <img
                    src={item.image || "/img/placeholder.webp"}
                    alt=""
                    className="w-9 h-9 object-contain rounded bg-gray-50 border"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.src = "/img/placeholder.webp";
                    }}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-gray-900 truncate">{item.name}</span>
                    {item.type === "product" && (item.brand || item.subtitle) && (
                      <span className="block text-xs text-gray-500 truncate">
                        {[item.brand, item.subtitle].filter(Boolean).join(" • ")}
                      </span>
                    )}
                  </span>
                  <span className="ml-auto text-gray-400 text-xs shrink-0">
                    {item.type === "category" ? "kategória" : "termék"}
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
